import type { ChangeRecord, SceneDef, StoryPack, SoloRunState } from '@plotjack/core';
import { useEffect, useRef } from 'react';
import { rewriteFor, whatChanged, fillVars } from '@plotjack/core';

interface Props {
  change: ChangeRecord;
  scene: SceneDef;
  story: StoryPack;
  state: SoloRunState;
  bridge?: string;
  quip?: string;
}

/** The answer to "what did my card just do": target, struck narrator line, rewrite, plain deltas. */
export function WhatChanged({ change, scene, story, state, bridge, quip }: Props) {
  const ref = useRef<HTMLElement>(null);
  const playKey = `${change.sceneIndex}-${(state.history ?? []).length}`;
  // Once the card has landed, bring the panel above the sticky Play bar so it is never hidden behind it.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const t = window.setTimeout(() => {
      const bar = document.querySelector('.play-card-bar') as HTMLElement | null;
      const barH = bar?.getBoundingClientRect().height ?? 0;
      const r = el.getBoundingClientRect();
      const visibleBottom = window.innerHeight - barH;
      if (r.top < 0 || r.top > visibleBottom - Math.min(r.height, visibleBottom * 0.6)) {
        const target = window.scrollY + r.top - Math.max(12, Math.min(window.innerHeight * 0.18, visibleBottom - r.height - 12));
        window.scrollTo({ top: Math.max(0, target), behavior: reduced ? 'auto' : 'smooth' });
      }
    }, reduced ? 50 : 1500);
    return () => window.clearTimeout(t);
  }, [playKey]);
  const element = scene.elements.find((e) => e.id === change.elementId) ?? scene.elements[0];
  const vars = { ...(story.variables ?? {}), ...state.variables };
  const rw = rewriteFor(change, scene, element, story, state.variables);
  const playsThisScene = (state.history ?? []).filter((c) => c.sceneIndex === change.sceneIndex).length;
  // the rewrite already shows the change sentence unless it rewrote the plan instead
  const lines = whatChanged(change, story, { includeChange: rw.isPlan, playsThisScene });
  // the outcome text usually opens with the same sentence as the rewrite; show only what it adds
  let extra = bridge ? fillVars(bridge, vars).trim() : '';
  if (extra.startsWith(rw.after)) extra = extra.slice(rw.after.length).trim();
  if (extra.startsWith(change.text)) extra = extra.slice(change.text.length).trim();
  return (
    <section ref={ref} className={`what-changed what-changed--${change.cardType}`} aria-label="What changed" aria-live="polite">
      <p className="what-changed__head">
        <span className="what-changed__card">{change.cardName}</span>
        <span aria-hidden="true"> → </span>
        <mark className="target-mark target-mark--hit">{change.targetLabel}</mark>
      </p>
      <div className="rewrite">
        <p className="rewrite__label">{rw.isPlan ? "Narrator's plan" : 'Narrator wrote'}</p>
        <p className="rewrite__before">
          <del>{rw.before}</del>
        </p>
        <p className="rewrite__label">{rw.isPlan ? 'Now' : 'Rewritten'}</p>
        <p className="rewrite__after">{rw.after}</p>
      </div>
      {extra && <p className="bridge">{extra}</p>}
      {quip && <p className="quip">"{fillVars(quip, vars)}"</p>}
      <div className="what-changed__box">
        <p className="what-changed__title">What changed</p>
        <ul>
          {lines.map((l, i) => (
            <li key={i} className={`wc-line wc-line--${l.kind} ${(l.kind === 'dread' && (l.value ?? 0) > 0) || (l.kind === 'swing' && (l.value ?? 0) < 0) ? 'wc-line--bad' : ''}`}>
              {l.text}
            </li>
          ))}
        </ul>
        {change.plan === 'stands' && playsThisScene < 2 && (
          <p className="what-changed__tip">Tip: play one more card to bend the plan, or a Counter to block it. Letting a plan through unopposed costs the most Dread.</p>
        )}
      </div>
    </section>
  );
}
